// No imports here: index.tsx loads this into the initial chunk, which must stay small.

// Diagnostics recorded alongside a chunk load error, to work out why a stale tab lost its chunks
// (see NES-468). Each attribute separates one candidate cause:
// - controller replaced: a newer service worker activated and took over this tab. It only serves its
//   own build's chunks, so this build's chunks go to the network, which no longer has them
// - never controlled: the tab bypassed the service worker entirely (claimed_after_load means the tab
//   was the first page to install the worker, which is expected and not a cause on its own)
// - controlled, controller not replaced, own entry chunk missing from the cache: the cache was evicted
// - service workers unsupported: no service worker can help (e.g. non-TLS self-hosted instances)

const diagnosticsTimeoutMs = 500;

interface TrackedState {
    appEntryUrl: string | null;
    controlledAtLoad: boolean | null;
    claimedAfterLoadAt: number | null;
    controllerReplacedAt: number | null;
    hiddenSince: number | null;
    hiddenTotalMs: number;
}

const tracked: TrackedState = { appEntryUrl: null, controlledAtLoad: null, claimedAfterLoadAt: null, controllerReplacedAt: null, hiddenSince: null, hiddenTotalMs: 0 };

// Call once at startup, so service worker control and tab visibility are captured from the start of the tab's life.
export function trackChunkLoadDiagnostics(appEntryUrl: string) {
    tracked.appEntryUrl = appEntryUrl;

    if ("serviceWorker" in navigator) {
        tracked.controlledAtLoad = navigator.serviceWorker.controller !== null;
        let hadController = tracked.controlledAtLoad;
        navigator.serviceWorker.addEventListener("controllerchange", () => {
            if (hadController) {
                tracked.controllerReplacedAt = performance.now();
            } else {
                tracked.claimedAfterLoadAt = performance.now();
            }
            hadController = navigator.serviceWorker.controller !== null;
        });
    }

    tracked.hiddenSince = document.visibilityState === "hidden" ? performance.now() : null;
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
            tracked.hiddenSince = performance.now();
        } else if (tracked.hiddenSince !== null) {
            tracked.hiddenTotalMs += performance.now() - tracked.hiddenSince;
            tracked.hiddenSince = null;
        }
    });
}

export type ChunkLoadDiagnostics = Record<string, string | number | boolean>;

// Gathers the diagnostics for a chunk load error. Reading the service worker and cache state is async,
// so it is capped: the caller reloads afterwards and must not be held up by a slow or hung browser API.
export async function collectChunkLoadDiagnostics(): Promise<ChunkLoadDiagnostics> {
    const now = performance.now();
    const hiddenTotalMs = tracked.hiddenTotalMs + (tracked.hiddenSince !== null ? now - tracked.hiddenSince : 0);
    const diagnostics: ChunkLoadDiagnostics = {
        "service_worker.supported": "serviceWorker" in navigator && globalThis.isSecureContext === true,
        "tab.age_s": Math.round(now / 1000),
        "tab.hidden_s": Math.round(hiddenTotalMs / 1000),
        "service_worker.claimed_after_load": tracked.claimedAfterLoadAt !== null,
        "service_worker.controller_replaced": tracked.controllerReplacedAt !== null,
    };
    if (tracked.controlledAtLoad !== null) {
        diagnostics["service_worker.controlled_at_load"] = tracked.controlledAtLoad;
    }
    if (tracked.controllerReplacedAt !== null) {
        diagnostics["service_worker.controller_replaced_s_ago"] = Math.round((now - tracked.controllerReplacedAt) / 1000);
    }

    try {
        const browserState = await withTimeout(readServiceWorkerAndCacheState(), diagnosticsTimeoutMs);
        return browserState === "timed-out" ? { ...diagnostics, "chunk_load.diagnostics_timed_out": true } : { ...diagnostics, ...browserState };
    } catch {
        // e.g. Cache Storage can throw a SecurityError when site data is blocked
        return { ...diagnostics, "chunk_load.diagnostics_failed": true };
    }
}

async function readServiceWorkerAndCacheState(): Promise<ChunkLoadDiagnostics> {
    const state: ChunkLoadDiagnostics = {};

    if ("serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        state["service_worker.controlled_now"] = navigator.serviceWorker.controller !== null;
        state["service_worker.active"] = registration?.active?.state ?? "none";
        state["service_worker.waiting"] = registration?.waiting?.state ?? "none";
        state["service_worker.installing"] = registration?.installing?.state ?? "none";
    }

    if (typeof caches !== "undefined") {
        const cacheNames = await caches.keys();
        let entryCount = 0;
        for (const cacheName of cacheNames) {
            entryCount += (await (await caches.open(cacheName)).keys()).length;
        }
        state["precache.cache_count"] = cacheNames.length;
        state["precache.entry_count"] = entryCount;
        if (tracked.appEntryUrl !== null) {
            // Workbox stores precached URLs with a revision query string, so ignore the search part.
            state["precache.own_entry_cached"] = (await caches.match(tracked.appEntryUrl, { ignoreSearch: true })) !== undefined;
        }
    }

    return state;
}

export async function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T | "timed-out"> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<"timed-out">((resolve) => {
        timer = setTimeout(() => resolve("timed-out"), timeoutMs);
    });
    try {
        return await Promise.race([promise, timeout]);
    } finally {
        clearTimeout(timer);
    }
}
